import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, API } from '../../lib/api';
import { toast } from 'sonner';
import {
  KeyRound, Plus, RefreshCw, Trash2, Copy, Check, BarChart3, Link2, Info,
} from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const STATUS_STYLE = {
  active: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-amber-100 text-amber-700',
  revoked: 'bg-slate-200 text-slate-500',
};

export default function AnalyticsBI() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const baseUrl = `${API}/analytics`;

  const load = () => {
    setLoading(true);
    apiGet('/analytics/tokens')
      .then((d) => setTokens(d.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load tokens'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Give the token a name (e.g. the analyst / dashboard).'); return; }
    setCreating(true);
    try {
      const r = await apiPost('/analytics/token', { name: name.trim() });
      setNewToken(r.token);
      setName('');
      toast.success('Token created — copy it now');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create token'); }
    setCreating(false);
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this token? Any Power BI dashboard using it will stop refreshing immediately.')) return;
    try { await apiPost(`/analytics/tokens/${id}/revoke`); toast.success('Token revoked'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const renew = async (id) => {
    if (!window.confirm('Renew this token? A new token is issued and the old one is revoked immediately.')) return;
    try {
      const r = await apiPost(`/analytics/tokens/${id}/renew`);
      setNewToken(r.token);
      toast.success('Renewed — copy the new token now');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const inp = 'px-3 py-2 border border-slate-300 rounded-sm text-sm';

  return (
    <div data-testid="analytics-bi-page">
      <PageHeader overline="Settings · Integrations" title="Analytics & BI · Power BI"
        subtitle="Issue long-lived tokens so Power BI / Excel can pull live dimensional data from Ellora ERP." />

      {/* Connection guide */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <h3 className="font-display font-bold text-base flex items-center gap-2 mb-2"><Link2 size={16} className="text-[#1F7A6E]" /> How to connect Power BI</h3>
        <ol className="text-xs text-slate-600 space-y-1.5 list-decimal ml-4">
          <li>Create a named token below and copy it (shown only once).</li>
          <li>In Power BI Desktop: <b>Get Data → Web → Advanced</b>.</li>
          <li>URL: <code className="bg-slate-100 px-1 rounded" data-testid="analytics-base-url">{baseUrl}/projects</code> (or any dataset from the schema).</li>
          <li>Add an HTTP header — name <code className="bg-slate-100 px-1 rounded">Authorization</code>, value <code className="bg-slate-100 px-1 rounded">Bearer &lt;your-token&gt;</code>.</li>
        </ol>
        <div className="mt-3 flex flex-wrap gap-2">
          {['projects', 'costs', 'procurement', 'hr', 'safety', 'tendering', 'schema'].map((d) => (
            <span key={d} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm bg-slate-100 text-slate-600" data-testid={`analytics-dataset-${d}`}>
              <BarChart3 size={11} /> /analytics/{d}
            </span>
          ))}
        </div>
      </div>

      {/* Create token */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <h3 className="font-display font-bold text-base flex items-center gap-2 mb-3"><KeyRound size={16} className="text-[#1F7A6E]" /> Issue a Token</h3>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-xs text-slate-500 flex-1 min-w-[220px]">
            <span className="block font-display font-bold uppercase tracking-widest text-[10px] mb-1">Token name (analyst / dashboard)</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rohan — Cost Dashboard"
              className={inp + ' w-full'} data-testid="analytics-token-name" />
          </label>
          <button onClick={create} disabled={creating} data-testid="analytics-create-token"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-[#1F7A6E] text-white hover:bg-[#2f3a5c] disabled:opacity-50">
            <Plus size={13} /> {creating ? 'Creating…' : 'Create Token'}
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Info size={11} /> Tokens are valid for 1 year. Only a hash is stored — we can never show it again after creation.</p>

        {newToken && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="analytics-new-token">
            <div className="text-[11px] font-display font-bold uppercase tracking-widest text-amber-700 mb-1">Copy now — shown only once</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm break-all">{newToken}</code>
              <button onClick={() => copy(newToken)} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-white border border-amber-300" data-testid="analytics-copy-token">
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Token list */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display font-bold text-base mb-3">Active &amp; Past Tokens</h3>
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : tokens.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="analytics-no-tokens">No tokens issued yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-tokens-table">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b">
                <th className="py-2">Name</th><th>Token</th><th>Status</th><th>Expires</th><th>Last Used</th><th>Created By</th><th></th>
              </tr></thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100" data-testid={`analytics-token-${t.id}`}>
                    <td className="py-2 font-display font-semibold text-slate-800">{t.name}</td>
                    <td className="font-mono text-xs text-slate-400">{t.masked}</td>
                    <td><span className={`text-[10px] px-1.5 py-0.5 rounded font-display font-bold uppercase ${STATUS_STYLE[t.status] || ''}`}>{t.status}</span></td>
                    <td className="text-xs text-slate-500">{t.status === 'active' ? `in ${t.days_left}d` : fmt(t.expires_at)}</td>
                    <td className="text-xs text-slate-500">{t.last_used_at ? fmt(t.last_used_at) : 'never'}</td>
                    <td className="text-xs text-slate-500">{t.created_by}</td>
                    <td className="text-right whitespace-nowrap">
                      {t.status !== 'revoked' && (
                        <>
                          <button onClick={() => renew(t.id)} title="Renew" data-testid={`analytics-renew-${t.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-slate-300 hover:bg-slate-50 mr-1"><RefreshCw size={12} /> Renew</button>
                          <button onClick={() => revoke(t.id)} title="Revoke" data-testid={`analytics-revoke-${t.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 size={12} /> Revoke</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
